"use server";

// ============================================================
// CLUBHUB – Pinnwand speichern (Server Actions)
// ============================================================
// Wie bei den Gruppen: ohne Sitzung (Musterbestand) pruefen und Erfolg
// melden, nichts schreiben — der Bestand im Browser traegt die
// Aenderung. Mit Sitzung schreiben sie in den Verein der Sitzung
// (`requirePermission("board.post")`). Die Arbeit macht
// `lib/pinnwand/speicher.ts`.
// ============================================================
import { revalidatePath } from "next/cache";
import { aktuell } from "@/lib/current";
import type { ActionResult } from "@/lib/action-result";
import type { TaskStatus } from "@/generated/prisma/enums";
import type { PrismaClient } from "@/generated/prisma/client";
import type { AufgabeEingabe, AufgabeView, AushangEingabe, AushangView } from "@/lib/pinnwand/views";
import { isoHeute, isoJetzt } from "@/lib/gruppen/datum";
import {
  Abgelehnt,
  hefteAushangAn,
  legeAufgabeAb,
  loescheAufgabe,
  loescheAushang,
  pruefeAufgabe,
  pruefeAushang,
  setzeAufgabenStatus,
  speichereAufgabe,
  speichereAushang,
  type Wer,
} from "@/lib/pinnwand/speicher";

function neuLaden() {
  revalidatePath("/pinnwand", "layout");
  revalidatePath("/");
}

function alsFehler(e: unknown): ActionResult | null {
  if (e instanceof Abgelehnt) return e.field ? { error: e.message, field: e.field } : { error: e.message };
  return null;
}

async function imMuster<T extends object>(ergebnis: () => T): Promise<ActionResult<T> | null> {
  if ((await aktuell()).quelle !== "muster") return null;
  try {
    return { success: true, ...ergebnis() };
  } catch (e) {
    const f = alsFehler(e);
    if (f) return f as ActionResult<T>;
    throw e;
  }
}

async function mitRecht<T extends object>(tun: (db: PrismaClient, wer: Wer) => Promise<T>): Promise<ActionResult<T>> {
  const [{ requirePermission, Forbidden }, { db }] = await Promise.all([import("@/lib/tenant"), import("@/lib/db")]);
  let wer: Wer;
  try {
    const ctx = await requirePermission("board.post");
    wer = { clubId: ctx.clubId, userId: ctx.userId };
  } catch (e) {
    if (e instanceof Forbidden) return { error: "Dafür fehlt dir das Recht, an die Pinnwand zu schreiben." };
    throw e;
  }
  try {
    const r = await tun(db, wer);
    neuLaden();
    return { success: true, ...r };
  } catch (e) {
    const f = alsFehler(e);
    if (f) return f as ActionResult<T>;
    throw e;
  }
}

const kennung = (id: unknown) => (typeof id === "string" && id.length > 0 && id.length <= 64 ? id : null);

// ── Aushaenge ────────────────────────────────────────────────

export async function aushangSpeichern(id: string | null, eingabe: AushangEingabe): Promise<ActionResult<{ aushang: AushangView }>> {
  const heute = isoHeute();
  const muster = await imMuster(() => {
    const e = pruefeAushang(eingabe, heute);
    const jetzt = isoJetzt();
    return { aushang: { id: kennung(id) ?? "p-muster", ...e, authorId: null, authorName: "", createdAt: jetzt, updatedAt: jetzt } };
  });
  if (muster) return muster;
  if (id !== null && !kennung(id)) return { error: "Diesen Aushang gibt es nicht (mehr)." };
  return mitRecht(async (db, wer) => ({ aushang: await speichereAushang(db, wer, id === null ? null : kennung(id), eingabe, heute) }));
}

export async function aushangAnheften(id: string, an: boolean): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht(async (db, wer) => {
      await hefteAushangAn(db, wer, id, an);
      return {};
    })
  );
}

export async function aushangLoeschen(id: string): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht(async (db, wer) => {
      await loescheAushang(db, wer, id);
      return {};
    })
  );
}

// ── Aufgaben ─────────────────────────────────────────────────

export async function aufgabeSpeichern(id: string | null, eingabe: AufgabeEingabe): Promise<ActionResult<{ aufgabe: AufgabeView }>> {
  const heute = isoHeute();
  const muster = await imMuster(() => {
    const e = pruefeAufgabe(eingabe);
    const jetzt = isoJetzt();
    return {
      aufgabe: { id: kennung(id) ?? "t-muster", ...e, eventId: null, eventTitle: null, createdByName: "", createdAt: jetzt, updatedAt: jetzt },
    };
  });
  if (muster) return muster;
  if (id !== null && !kennung(id)) return { error: "Diese Aufgabe gibt es nicht (mehr)." };
  return mitRecht(async (db, wer) => ({ aufgabe: await speichereAufgabe(db, wer, id === null ? null : kennung(id), eingabe, heute) }));
}

export async function aufgabeStatusSetzen(id: string, status: TaskStatus): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht(async (db, wer) => {
      await setzeAufgabenStatus(db, wer, id, status);
      return {};
    })
  );
}

/** Ins Brett abgelegt: neuer Stand und die Reihenfolge der Zielspalte (IDs von oben nach unten). */
export async function aufgabeAblegen(id: string, status: TaskStatus, reihenfolge: string[]): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht(async (db, wer) => {
      await legeAufgabeAb(db, wer, id, status, reihenfolge);
      return {};
    })
  );
}

export async function aufgabeLoeschen(id: string): Promise<ActionResult> {
  return (
    (await imMuster(() => ({}))) ??
    mitRecht(async (db, wer) => {
      await loescheAufgabe(db, wer, id);
      return {};
    })
  );
}
